import React, { useState, useEffect, useCallback } from "react";
import {
  X, CalendarDays, ChevronLeft, ChevronRight, Plus, RefreshCw,
  Users, Phone, Copy, Check, Armchair, StickyNote,
} from "lucide-react";
import api from "../utils/api";
import { toast } from "react-toastify";
import { useAuth } from "./authentication/AuthContext";

const STATUS_META = {
  PENDING:   { label: "Pending",   chip: "bg-amber-100 text-amber-700 border-amber-200" },
  CONFIRMED: { label: "Confirmed", chip: "bg-emerald-100 text-emerald-700 border-emerald-200" },
  SEATED:    { label: "Seated",    chip: "bg-blue-100 text-blue-700 border-blue-200" },
  COMPLETED: { label: "Done",      chip: "bg-slate-100 text-slate-600 border-slate-200" },
  CANCELLED: { label: "Cancelled", chip: "bg-rose-100 text-rose-600 border-rose-200" },
  NO_SHOW:   { label: "No-show",   chip: "bg-slate-200 text-slate-500 border-slate-300" },
};

// Actions available from each status
const NEXT_ACTIONS = {
  PENDING:   [{ to: "CONFIRMED", label: "Confirm", cls: "bg-emerald-600 text-white" }, { to: "CANCELLED", label: "Decline", cls: "bg-rose-100 text-rose-600" }],
  CONFIRMED: [{ to: "SEATED", label: "Seat now", cls: "bg-blue-600 text-white" }, { to: "NO_SHOW", label: "No-show", cls: "bg-slate-100 text-slate-500" }, { to: "CANCELLED", label: "Cancel", cls: "bg-rose-100 text-rose-600" }],
  SEATED:    [{ to: "COMPLETED", label: "Complete", cls: "bg-slate-800 text-white" }],
};

const todayIST = () => new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });

const shiftDate = (dateStr, days) => {
  const d = new Date(`${dateStr}T00:00:00`);
  d.setDate(d.getDate() + days);
  return d.toLocaleDateString("en-CA");
};

const fmtTime = (t) => {
  const [h, m] = String(t).split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = h % 12 || 12;
  return `${hr}:${String(m || 0).padStart(2, "0")} ${ampm}`;
};

const EMPTY_FORM = {
  customerName: "", customerMobile: "", partySize: 2,
  date: todayIST(), time: "19:00", durationMinutes: 90,
  floorName: "", sectionName: "", unitNumber: "", notes: "",
};

export default function ReservationsPanel({ onClose }) {
  const { restaurantId } = useAuth();
  const [date, setDate] = useState(todayIST());
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [layout, setLayout] = useState(null);
  const [slug, setSlug] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchReservations = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await api.get(`/reservations?date=${d}`);
      setReservations(res.data.reservations || []);
    } catch {
      toast.error("Failed to load reservations");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchReservations(date); }, [date, fetchReservations]);

  useEffect(() => {
    api.get("/layout").then(res => setLayout(res.data)).catch(() => {});
    if (restaurantId) {
      api.get(`/user/${restaurantId}`).then(res => setSlug(res.data?.slug || null)).catch(() => {});
    }
  }, [restaurantId]);

  const bookingLink = slug ? `${window.location.origin}/#/book/${slug}` : null;

  const copyLink = () => {
    if (!bookingLink) return;
    navigator.clipboard.writeText(bookingLink).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/reservations/${id}`, { status });
      fetchReservations(date);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update");
    }
  };

  const handleCreate = async () => {
    if (!form.customerName.trim() || !form.customerMobile.trim()) {
      toast.error("Name and mobile are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reservations", {
        ...form,
        partySize: Number(form.partySize) || 1,
        unitNumber: form.unitNumber ? Number(form.unitNumber) : null,
      });
      toast.success("Reservation created");
      setShowNew(false);
      setForm({ ...EMPTY_FORM, date });
      fetchReservations(date);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to create reservation");
    } finally {
      setSubmitting(false);
    }
  };

  // Sections of the selected floor (for optional table assignment)
  const floorObj = layout?.floors?.find(f => f.floorName === form.floorName);
  const sectionObj = floorObj?.sections?.find(s => s.name === form.sectionName);

  const counts = reservations.reduce((acc, r) => {
    acc[r.status] = (acc[r.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[92vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-500/15 border border-indigo-500/25 flex items-center justify-center">
              <CalendarDays size={15} className="text-indigo-500" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-slate-800">Reservations</h2>
              <p className="text-[10px] text-slate-400">Table bookings & walk-in planning</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setForm({ ...EMPTY_FORM, date }); setShowNew(v => !v); }}
              className="flex items-center gap-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded-xl transition-colors"
            >
              <Plus size={12} /> New Booking
            </button>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Date nav */}
        <div className="flex items-center gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/50">
          <button onClick={() => setDate(d => shiftDate(d, -1))} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50">
            <ChevronLeft size={14} />
          </button>
          <input
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            className="border border-slate-200 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          />
          <button onClick={() => setDate(d => shiftDate(d, 1))} className="w-7 h-7 flex items-center justify-center rounded-lg bg-white border border-slate-200 text-slate-500 hover:bg-slate-50">
            <ChevronRight size={14} />
          </button>
          {date !== todayIST() && (
            <button onClick={() => setDate(todayIST())} className="text-[10px] font-bold text-indigo-600 hover:text-indigo-700">
              Today
            </button>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            {counts.PENDING > 0 && (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">
                {counts.PENDING} pending
              </span>
            )}
            <span className="text-[10px] text-slate-400 font-semibold">{reservations.length} total</span>
          </div>
        </div>

        {/* New booking form */}
        {showNew && (
          <div className="border-b border-slate-100 px-5 py-4 space-y-3 bg-indigo-50/40">
            <p className="text-[10px] font-black uppercase tracking-wider text-slate-500">New reservation</p>
            <div className="grid grid-cols-2 gap-2">
              <input placeholder="Customer name *" value={form.customerName} onChange={e => setForm(f => ({ ...f, customerName: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              <input placeholder="Mobile *" value={form.customerMobile} onChange={e => setForm(f => ({ ...f, customerMobile: e.target.value }))}
                className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />
            </div>
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="text-[9px] font-bold text-slate-400 uppercase">Guests</label>
                <input type="number" min="1" value={form.partySize} onChange={e => setForm(f => ({ ...f, partySize: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="text-[9px] font-bold text-slate-400 uppercase">Date</label>
                <input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="text-[9px] font-bold text-slate-400 uppercase">Time</label>
                <input type="time" value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="text-[9px] font-bold text-slate-400 uppercase">Mins</label>
                <input type="number" min="15" step="15" value={form.durationMinutes} onChange={e => setForm(f => ({ ...f, durationMinutes: e.target.value }))}
                  className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
            </div>

            {/* Optional table assignment */}
            {layout?.floors?.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                <select value={form.floorName} onChange={e => setForm(f => ({ ...f, floorName: e.target.value, sectionName: "", unitNumber: "" }))}
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500">
                  <option value="">Floor (optional)</option>
                  {layout.floors.map(f => <option key={f.floorName} value={f.floorName}>{f.floorName}</option>)}
                </select>
                <select value={form.sectionName} disabled={!floorObj} onChange={e => setForm(f => ({ ...f, sectionName: e.target.value, unitNumber: "" }))}
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50">
                  <option value="">Section</option>
                  {(floorObj?.sections || []).map(s => <option key={s.name} value={s.name}>{s.name}</option>)}
                </select>
                <select value={form.unitNumber} disabled={!sectionObj} onChange={e => setForm(f => ({ ...f, unitNumber: e.target.value }))}
                  className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-indigo-500 disabled:opacity-50">
                  <option value="">Table #</option>
                  {sectionObj && Array.from({ length: sectionObj.totalUnits }, (_, i) => i + 1).map(u => (
                    <option key={u} value={u}>{sectionObj.name} {u}</option>
                  ))}
                </select>
              </div>
            )}

            <input placeholder="Notes (occasion, preferences…)" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />

            <div className="flex justify-end gap-2">
              <button onClick={() => setShowNew(false)} className="px-3 py-1.5 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200">Cancel</button>
              <button onClick={handleCreate} disabled={submitting}
                className="px-4 py-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl disabled:opacity-40 transition-colors">
                {submitting ? "Saving…" : "Save Booking"}
              </button>
            </div>
          </div>
        )}

        {/* List */}
        <div className="overflow-y-auto flex-1 px-5 py-4 space-y-2.5">
          {loading ? (
            <div className="flex items-center justify-center py-10">
              <RefreshCw size={18} className="animate-spin text-slate-400" />
            </div>
          ) : reservations.length === 0 ? (
            <div className="text-center py-10">
              <CalendarDays size={32} className="mx-auto text-slate-200 mb-2" />
              <p className="text-xs text-slate-400">No reservations for this date</p>
            </div>
          ) : (
            reservations.map(r => {
              const meta = STATUS_META[r.status] || STATUS_META.CONFIRMED;
              const actions = NEXT_ACTIONS[r.status] || [];
              return (
                <div key={r._id} className="rounded-xl border border-slate-200 p-3 bg-white">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="text-center shrink-0 bg-indigo-50 border border-indigo-100 rounded-lg px-2 py-1">
                        <p className="text-xs font-black text-indigo-700 leading-tight">{fmtTime(r.time)}</p>
                        <p className="text-[9px] text-indigo-400">{r.durationMinutes || 90} min</p>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-slate-800">{r.customerName}</p>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                          <span className="flex items-center gap-1 text-[10px] text-slate-500"><Users size={10} /> {r.partySize} guests</span>
                          <span className="flex items-center gap-1 text-[10px] text-slate-500"><Phone size={10} /> {r.customerMobile}</span>
                          {r.unitNumber && (
                            <span className="flex items-center gap-1 text-[10px] text-slate-500">
                              <Armchair size={10} /> {r.sectionName} {r.unitNumber}{r.floorName ? ` · ${r.floorName}` : ""}
                            </span>
                          )}
                        </div>
                        {r.notes && (
                          <p className="flex items-center gap-1 text-[10px] text-slate-400 mt-1"><StickyNote size={10} /> {r.notes}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.chip}`}>
                        {meta.label}{r.source === "PUBLIC" ? " · online" : ""}
                      </span>
                    </div>
                  </div>
                  {actions.length > 0 && (
                    <div className="flex justify-end gap-1.5 mt-2 pt-2 border-t border-slate-50">
                      {actions.map(a => (
                        <button key={a.to} onClick={() => setStatus(r._id, a.to)}
                          className={`text-[10px] font-bold px-2.5 py-1 rounded-lg transition-opacity hover:opacity-80 ${a.cls}`}>
                          {a.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer: public booking link */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          {bookingLink ? (
            <div className="flex items-center gap-2 flex-1 min-w-0">
              <span className="text-[10px] font-bold text-slate-400 uppercase shrink-0">Booking link</span>
              <code className="text-[10px] font-mono text-slate-600 truncate flex-1">{bookingLink}</code>
              <button onClick={copyLink} title="Copy booking link"
                className="shrink-0 w-6 h-6 flex items-center justify-center rounded-md bg-slate-100 hover:bg-slate-200 text-slate-500">
                {copied ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} />}
              </button>
            </div>
          ) : <span className="text-[10px] text-slate-300">Share your booking link with customers</span>}
          <button onClick={onClose} className="px-4 py-2 text-xs font-semibold bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 shrink-0">Close</button>
        </div>
      </div>
    </div>
  );
}
